import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { BUSINESS, IMAGES } from "@/data";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-[var(--line)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="Magical Cleaning Team logo" className="h-9 w-9" />
          <span className="font-display text-lg font-extrabold leading-none tracking-tight">
            Magical<span className="text-[var(--brand)]">.</span>
            <span className="hidden sm:inline"> Cleaning Team</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-9 lg:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={`nav-link text-sm font-semibold ${
                pathname === n.to ? "text-[var(--brand)]" : "text-[var(--ink)]"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneHref}`}
            data-testid="nav-call-btn"
            className="hidden items-center gap-2 text-sm font-semibold text-[var(--ink)] sm:flex nav-link"
          >
            <Phone className="h-4 w-4 text-[var(--brand)]" />
            {BUSINESS.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="nav-quote-btn"
            className="sweep-btn hidden bg-[var(--brand)] px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-transform duration-300 hover:-translate-y-0.5 md:inline-flex"
          >
            <span className="relative z-10">Get Free Quote</span>
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="grid h-10 w-10 place-items-center border border-[var(--line)] lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-[var(--line)] bg-white lg:hidden">
          <div className="flex flex-col px-6 py-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-link-${n.label.toLowerCase()}`}
                className="border-b border-[var(--line)] py-4 font-display text-2xl font-bold tracking-tight"
              >
                {n.label}
              </Link>
            ))}
            <a href={`tel:${BUSINESS.phoneHref}`} className="mt-4 flex items-center gap-2 font-semibold text-[var(--brand)]">
              <Phone className="h-4 w-4" /> {BUSINESS.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
